import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { buildWhatsappLink } from '@/constants/site';

const STORAGE_KEY = 'bb-promo-seen';
const OFFER_MSG =
  'I found this offer on the website, I want to claim the offer — 3 suits for AED 3333.';

/**
 * PromoModal — auto-shows a promotional banner 2s after the page mounts.
 * - Once dismissed, sessionStorage suppresses it for the rest of the browser session.
 * - Clicking the banner (or the "WhatsApp Now" button in the artwork) opens
 *   WhatsApp with a prefilled offer message and fires the GTM `Whatsapp` event.
 */
export default function PromoModal({ delay = 2000 }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      if (window.sessionStorage.getItem(STORAGE_KEY) === '1') return;
    } catch (e) { /* private mode etc. */ }

    const t = window.setTimeout(() => setOpen(true), delay);
    return () => window.clearTimeout(t);
  }, [delay]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    // Lock body scroll while open
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [open]);

  const close = () => {
    setOpen(false);
    try { window.sessionStorage.setItem(STORAGE_KEY, '1'); } catch (e) {}
  };

  const handleClaim = () => {
    if (window.gtag) window.gtag('event', 'Whatsapp', { source: 'promo-modal' });
    close();
  };

  if (!open) return null;

  const waHref = buildWhatsappLink(OFFER_MSG);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="End of Season Offer"
      data-testid="promo-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8 bg-black/80 backdrop-blur-sm animate-[bb-fade_.35s_ease-out]"
      onClick={close}
    >
      <div
        className="relative w-full max-w-[900px] shadow-[0_25px_80px_-20px_rgba(0,0,0,0.8)] animate-[bb-slide_.5s_cubic-bezier(0.22,1,0.36,1)]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          type="button"
          aria-label="Close offer"
          data-testid="promo-close"
          onClick={close}
          className="absolute -top-3 -right-3 md:-top-4 md:-right-4 z-10 w-9 h-9 md:w-11 md:h-11 rounded-full bg-[hsl(var(--bb-charcoal))] text-[hsl(var(--bb-gold))] border border-[hsl(var(--bb-gold))]/60 flex items-center justify-center hover:bg-[hsl(var(--bb-gold))] hover:text-[hsl(var(--bb-charcoal))] transition-colors duration-300"
        >
          <X size={18} />
        </button>

        {/* Banner (whole thing is a WhatsApp link) */}
        <a
          href={waHref}
          target="_blank"
          rel="noreferrer"
          data-testid="promo-cta"
          onClick={handleClaim}
          className="block relative overflow-hidden group"
        >
          <picture>
            <source srcSet="/assets/promo-suits.webp" type="image/webp" />
            <img
              src="/assets/promo-suits.jpg"
              alt="End of Season Offer — Get 3 Men Suits for AED 3333"
              className="w-full h-auto block select-none"
              draggable="false"
            />
          </picture>
          {/* Subtle hover cue */}
          <span className="absolute inset-0 ring-0 ring-[hsl(var(--bb-gold))]/0 group-hover:ring-2 group-hover:ring-[hsl(var(--bb-gold))]/60 transition-all duration-500 pointer-events-none" />
        </a>
      </div>

      <style>{`
        @keyframes bb-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes bb-slide {
          from { opacity: 0; transform: translateY(24px) scale(0.98); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
